import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  IconButton,
  MobileStepper,
  Dialog,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { motion, useReducedMotion, useMotionValue, useSpring } from "framer-motion";
import EarlyMemberPlanSection from "./EarlyMemberPlanSection";
import CitizenFirstStatement from "./CitizenFirstStatement";
import { useJoinMotion } from "./JoinMotion";
import { useFeatureTabShader } from "./featureTabShader";
import { getFeaturePreviews, audienceFromIdPrefix } from "../../utils/featureAssets";

const PILL_SPRING = { stiffness: 380, damping: 36, mass: 0.9 };
const PILL_PARALLAX_SPRING = { stiffness: 260, damping: 28, mass: 0.7 };
const PILL_TRANSITION_MS = 420;

const FEATURE_TAB_CHIP_BASE = {
  position: "relative",
  zIndex: 1,
  borderRadius: "999px",
  px: 2,
  py: 1,
  minHeight: 44,
  fontSize: 13,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
  letterSpacing: "0.01em",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  transition: "color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
  "&:active": { transform: "scale(0.97)" },
  "&:focus-visible": { outline: "none", boxShadow: "var(--join-focus-ring)" },
};

function featureTabChipSx(active) {
  if (active) {
    return {
      ...FEATURE_TAB_CHIP_BASE,
      border: "1px solid transparent",
      bgcolor: "transparent",
      color: "#ffffff",
      boxShadow: "none",
      zIndex: 2,
    };
  }
  return {
    ...FEATURE_TAB_CHIP_BASE,
    border: "1px solid var(--join-border-muted)",
    bgcolor: "rgba(255, 255, 255, 0.85)",
    color: "var(--primary-color-hover)",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
    "&:hover": {
      bgcolor: "rgba(4, 120, 87, 0.06)",
      borderColor: "rgba(4, 120, 87, 0.28)",
      color: "var(--primary-color-hover)",
    },
  };
}

function isCarouselWrap(prevIndex, nextIndex, slideCount) {
  return (
    (prevIndex === slideCount - 1 && nextIndex === 0) ||
    (prevIndex === 0 && nextIndex === slideCount - 1)
  );
}

function FeatureTabSelector({
  columns,
  slides,
  activeIndex,
  onSelect,
  idPrefix,
  ariaLabel,
}) {
  const reduceMotion = useReducedMotion();
  const containerRef = useRef(null);
  const pillRef = useRef(null);
  const tabRefs = useRef([]);
  const initialMeasureDone = useRef(false);
  const prevIndexRef = useRef(activeIndex);
  const lastWrapRef = useRef(false);
  const [pillReady, setPillReady] = useState(false);
  const [pillStable, setPillStable] = useState(true);

  const pillLeft = useMotionValue(0);
  const pillTop = useMotionValue(0);
  const pillWidth = useMotionValue(0);
  const pillHeight = useMotionValue(0);
  const parallaxX = useMotionValue(0);
  const parallaxY = useMotionValue(0);

  const springLeft = useSpring(pillLeft, reduceMotion ? { duration: 0 } : PILL_SPRING);
  const springTop = useSpring(pillTop, reduceMotion ? { duration: 0 } : PILL_SPRING);
  const springWidth = useSpring(pillWidth, reduceMotion ? { duration: 0 } : PILL_SPRING);
  const springHeight = useSpring(pillHeight, reduceMotion ? { duration: 0 } : PILL_SPRING);
  const springParallaxX = useSpring(parallaxX, reduceMotion ? { duration: 0 } : PILL_PARALLAX_SPRING);
  const springParallaxY = useSpring(parallaxY, reduceMotion ? { duration: 0 } : PILL_PARALLAX_SPRING);

  const shaderEnabled = !reduceMotion;
  const { canvasRef, updatePointer, handlePointerLeave, syncCanvasSize, active: shaderActive } =
    useFeatureTabShader({
      enabled: shaderEnabled,
      pillRef,
      canResize: pillStable,
    });

  const measureActiveTab = useCallback(
    (instant = false) => {
      const container = containerRef.current;
      const tab = tabRefs.current[activeIndex];
      if (!container || !tab) return;

      const containerRect = container.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const next = {
        left: tabRect.left - containerRect.left,
        top: tabRect.top - containerRect.top,
        width: tabRect.width,
        height: tabRect.height,
      };

      pillLeft.set(next.left);
      pillTop.set(next.top);
      pillWidth.set(next.width);
      pillHeight.set(next.height);

      if (instant || !initialMeasureDone.current) {
        springLeft.jump(next.left);
        springTop.jump(next.top);
        springWidth.jump(next.width);
        springHeight.jump(next.height);
        initialMeasureDone.current = true;
      }

      setPillReady(next.width > 0);
    },
    [activeIndex, pillHeight, pillLeft, pillTop, pillWidth, springHeight, springLeft, springTop, springWidth]
  );

  useLayoutEffect(() => {
    const prevIndex = prevIndexRef.current;
    const wrapped = isCarouselWrap(prevIndex, activeIndex, slides.length);
    lastWrapRef.current = wrapped;
    measureActiveTab(reduceMotion || wrapped);
    prevIndexRef.current = activeIndex;
  }, [activeIndex, measureActiveTab, reduceMotion, slides.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const ro = new ResizeObserver(() => {
      measureActiveTab(true);
    });
    ro.observe(container);
    window.addEventListener("resize", measureActiveTab);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measureActiveTab);
    };
  }, [measureActiveTab]);

  useEffect(() => {
    if (reduceMotion) {
      setPillStable(true);
      return undefined;
    }

    if (lastWrapRef.current) {
      setPillStable(true);
      syncCanvasSize();
      return undefined;
    }

    setPillStable(false);
    const timer = window.setTimeout(() => {
      setPillStable(true);
      syncCanvasSize();
    }, PILL_TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [activeIndex, reduceMotion, syncCanvasSize]);

  const handleMouseMove = useCallback(
    (event) => {
      if (reduceMotion) return;
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const nx = (event.clientX - rect.left) / rect.width - 0.5;
      const ny = (event.clientY - rect.top) / rect.height - 0.5;
      parallaxX.set(nx * 2);
      parallaxY.set(ny * 1.2);
      updatePointer(event.clientX, event.clientY);
    },
    [reduceMotion, parallaxX, parallaxY, updatePointer]
  );

  const handleMouseLeave = useCallback(() => {
    handlePointerLeave();
    parallaxX.set(0);
    parallaxY.set(0);
  }, [handlePointerLeave, parallaxX, parallaxY]);

  return (
    <Box
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: "block",
        position: "relative",
        isolation: "isolate",
      }}
    >
      <Box
        ref={pillRef}
        aria-hidden
        component={motion.div}
        style={{
          left: springLeft,
          top: springTop,
          width: springWidth,
          height: springHeight,
        }}
        sx={{
          position: "absolute",
          borderRadius: "999px",
          pointerEvents: "none",
          zIndex: 0,
          overflow: "hidden",
          opacity: pillReady ? 1 : 0,
          background: "linear-gradient(160deg, #059669 0%, #047857 55%, #065f46 100%)",
          boxShadow: "0 1px 4px rgba(4, 120, 87, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.18)",
          willChange: "transform, width, height, left, top",
        }}
      >
        <Box
          component={motion.div}
          style={{
            x: springParallaxX,
            y: springParallaxY,
          }}
          sx={{
            position: "absolute",
            inset: "-2px",
            borderRadius: "inherit",
            overflow: "hidden",
          }}
        >
          {shaderActive && (
            <canvas
              ref={canvasRef}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                display: "block",
                pointerEvents: "none",
                opacity: pillStable ? 0.9 : 0,
                mixBlendMode: "soft-light",
                transition: "opacity 0.2s ease",
              }}
            />
          )}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              border: "1px solid rgba(255, 255, 255, 0.18)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 38%, transparent 100%)",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1} sx={{ position: "relative", isolation: "isolate" }}>
        {columns.map((col, colIndex) => {
          const firstItemIndex = slides.findIndex((s) => s.id === col.items[0].id);
          
          return (
            <Grid item xs={12} sm={4} key={col.title}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  alignItems: "stretch",
                  borderLeft: { sm: colIndex > 0 ? "1px solid var(--join-border-muted)" : "none" },
                  pl: { sm: colIndex > 0 ? 2 : 0 },
                }}
              >
                <Typography
                  component="button"
                  type="button"
                  onClick={() => onSelect(firstItemIndex)}
                  sx={{
                    fontSize: 13,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    color: "var(--black-color)",
                    mt: -1,
                    mb: 0.25,
                    width: "100%",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "center",
                    transition: "color 0.2s ease",
                    "&:hover": { color: "var(--primary-color-hover)" },
                    "&:focus-visible": { outline: "none", color: "var(--primary-color-hover)" },
                  }}
                >
                  {col.title}
                </Typography>
                {col.items.map((item) => {
                  const globalIndex = slides.findIndex((s) => s.id === item.id);
                  return (
                    <Box
                      key={item.id}
                      ref={(el) => {
                        tabRefs.current[globalIndex] = el;
                      }}
                      component="button"
                      type="button"
                      role="tab"
                      id={`${idPrefix}-feature-tab-${item.id}`}
                      aria-selected={globalIndex === activeIndex}
                      aria-controls={`${idPrefix}-feature-panel-${item.id}`}
                      onClick={() => onSelect(globalIndex)}
                      sx={{
                        ...featureTabChipSx(globalIndex === activeIndex),
                        width: "100%",
                      }}
                    >
                      {item.title}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

const CAROUSEL_NAV_BUTTON_SX = {
  minWidth: 48,
  minHeight: 48,
  borderRadius: "14px",
  border: "1px solid var(--join-border-muted)",
  bgcolor: "rgba(255, 255, 255, 0.9)",
  color: "var(--primary-color-hover)",
  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)",
  transition: "transform 0.12s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    bgcolor: "rgba(4, 120, 87, 0.08)",
    borderColor: "rgba(4, 120, 87, 0.35)",
    boxShadow: "0 6px 20px rgba(4, 120, 87, 0.12)",
  },
  "&:active": { transform: "scale(0.94)" },
  "&:focus-visible": { outline: "none", boxShadow: "var(--join-focus-ring)" },
};

const ORG_FEATURE_COLUMNS = [
  {
    title: "Campaigns",
    items: [
      {
        id: "campaigns",
        title: "Create campaigns",
        description:
          "Launch outreach campaigns, coordinate messaging, and keep your team aligned on civic goals.",
        subShots: [
          {
            title: "Create campaigns",
            description: "Launch outreach campaigns, coordinate messaging, and keep your team aligned on civic goals.",
          },
          {
            title: "Target bill",
            description: "Search bills in-app or add an external link so supporters know exactly what they're calling about.",
          },
          {
            title: "Who to call",
            description: "Quickly prefill targets from Congress or build a custom list for your outreach campaign.",
          },
        ],
      },
      {
        id: "take-action-pages",
        title: "Take action pages",
        description:
          "Publish a guided page for supporters — they're matched to their reps, get your script, and can call in one flow. Share by link or print a QR flyer.",
      },
      {
        id: "call-leaderboard",
        title: "Call leaderboard",
        description:
          "Motivate your phone bank with live call counts and rankings. See who's driving the most conversations, and let callers opt out of being visible on the leaderboard.",
      },
    ],
  },
  {
    title: "Events",
    items: [
      {
        id: "events",
        title: "Create events",
        description:
          "Create and manage events with registration, reminders, and turnout tracking in one place.",
      },
      {
        id: "qr-checkins",
        title: "QR check-ins",
        description:
          "Display separate attendee and volunteer check-in QR codes at your event. Supporters scan to check in on the spot for fast entry and accurate turnout counts.",
      },
      {
        id: "volunteers",
        title: "Volunteer management",
        description:
          "Coordinate volunteers, track interest, and manage assignments across your outreach.",
      },
      {
        id: "follow-up",
        title: "Follow-up actions",
        description:
          "Add post-event steps so participants know what to do next — petitions, calls, or follow-on events.",
      },
    ],
  },
  {
    title: "Connect",
    items: [
      {
        id: "verified-profile",
        title: "Verified presence",
        description:
          "Build trust with your audience through an official, verified profile and a dedicated community hub.",
      },
      {
        id: "polling",
        title: "Polling",
        description:
          "Pulse-check your community. Gather structured feedback and sentiment on local issues before making key decisions.",
      },
    ],
  },
];

const ORG_FEATURE_SLIDES = ORG_FEATURE_COLUMNS.flatMap((col) => col.items);

const CITIZEN_FEATURE_COLUMNS = [
  {
    title: "Personalized",
    items: [
      {
        id: "civic-dashboard",
        title: "Your civic dashboard",
        description:
          "See your representatives, upcoming elections, deadlines, and local civic activity in one personalized home.",
      },
      {
        id: "representatives",
        title: "Your reps",
        description:
          "Find the officials who represent your address, follow their work, and contact them directly inside CivixThread.",
      },
      {
        id: "elections",
        title: "Elections & deadlines",
        description:
          "Keep election dates, registration deadlines, ballot timelines, and district candidates together in one place.",
      },
    ],
  },
  {
    title: "Explore",
    items: [
      {
        id: "legislation",
        title: "Policy",
        description:
          "Search and follow federal or state legislation, then use clear analysis to understand the goals and tradeoffs.",
        subShots: [
          {
            title: "Policy",
            description:
              "Search and follow federal or state legislation, track sponsors, cosponsors, and key bill details in one place.",
          },
          {
            title: "Easy-to-read analysis",
            description:
              "Get a plain-language breakdown of a bill's objectives, tradeoffs, and conclusion without wading through legal text.",
          },
          {
            title: "Browse legislation",
            description:
              "Search federal bills, track status and sponsors, save bills you care about, and take action right from the list.",
          },
          {
            title: "Call your representatives",
            description:
              "Call your House member and senators about a bill, with your address matched to the right offices and phone numbers ready to dial.",
          },
          {
            title: "Write to your representatives",
            description:
              "Draft a message for their official contact form, copy your subject and text, and log your outreach after you send.",
          },
        ],
      },
      {
        id: "state-legislation",
        title: "State legislation",
        description:
          "Browse bills from your state legislature. Search, filter, and track session details, sponsors, and status alongside federal legislation.",
      },
      {
        id: "candidate-transparency",
        title: "Candidate transparency",
        description:
          "Explore candidate profiles, funding sources, campaign spending, and other context before casting your vote.",
      },
      {
        id: "public-comment",
        title: "Public comment periods",
        description:
          "Discover open government comment periods before they close and submit your input while decisions are still forming.",
      },
    ],
  },
  {
    title: "Participate",
    items: [
      {
        id: "local-events",
        title: "Civic events",
        description:
          "Find civic events, register, volunteer, and keep the next steps for your community involvement close at hand.",
      },
      {
        id: "community-feed",
        title: "Community feed",
        description:
          "Join conversations grounded in real bills, events, and local issues instead of disconnected political noise.",
        subShots: [
          {
            title: "Community feed",
            description:
              "Join conversations grounded in real bills, events, and local issues instead of disconnected political noise.",
          },
          {
            title: "Filter by your area",
            description:
              "Focus the feed on what matters near you — narrow posts to national, state, or district conversations, then refine by topic, post type, and who you follow.",
          },
          {
            title: "Action posts",
            description:
              "Share civic action tied to legislation and let others mark when they have taken action too, making collective participation visible.",
          },
        ],
      },
      {
        id: "civic-badges",
        title: "Civic badges",
        description:
          "Build a visible record of participation as you attend events, volunteer, learn, and take meaningful civic action.",
      },
    ],
  },
];

const CITIZEN_FEATURE_SLIDES = CITIZEN_FEATURE_COLUMNS.flatMap((col) => col.items);

// Carousel PNGs are 1080px wide with ~211px side padding around the device frame.
const CAROUSEL_IMAGE_WIDTH = 1080;
const CAROUSEL_DEVICE_WIDTH = 658;
const CAROUSEL_SIDE_PADDING = 211;
const CAROUSEL_VISIBLE_PHONE_WIDTH = { xs: 240, md: 280 };

const SCREENSHOT_NAV_BUTTON_SX = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 2,
  width: 44,
  height: 44,
  borderRadius: "50%",
  border: "1px solid var(--join-border-muted)",
  bgcolor: "rgba(255, 255, 255, 0.96)",
  color: "var(--primary-color-hover)",
  boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)",
  transition: "background-color 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    bgcolor: "#ffffff",
    borderColor: "rgba(4, 120, 87, 0.35)",
  },
  "&:focus-visible": { outline: "none", boxShadow: "var(--join-focus-ring)" },
};

function FeatureScreenshotPlaceholder({ title, previewImages = [], onShotIndexChange }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [shotIndex, setShotIndex] = useState(0);

  const shotCount = previewImages.length;
  const activeIndex = shotIndex < shotCount ? shotIndex : 0;
  const previewImage = previewImages[activeIndex];
  const hasMultipleShots = shotCount > 1;

  const showPreviousShot = useCallback(() => {
    setShotIndex((prev) => {
      const nextIndex = (prev - 1 + shotCount) % shotCount;
      if (onShotIndexChange) onShotIndexChange(nextIndex);
      return nextIndex;
    });
  }, [shotCount, onShotIndexChange]);

  const showNextShot = useCallback(() => {
    setShotIndex((prev) => {
      const nextIndex = (prev + 1) % shotCount;
      if (onShotIndexChange) onShotIndexChange(nextIndex);
      return nextIndex;
    });
  }, [shotCount, onShotIndexChange]);

  const imageAlt = hasMultipleShots
    ? `${title} preview ${activeIndex + 1} of ${shotCount}`
    : `${title} preview`;

  if (previewImage) {
    return (
      <>
        <Box sx={{ position: "relative", width: "100%", maxWidth: CAROUSEL_VISIBLE_PHONE_WIDTH, ml: { xs: "auto", md: 0 }, mr: { xs: "auto", md: "auto" } }}>
          <Box
            sx={{
              width: "100%",
              overflow: "hidden",
              bgcolor: "#ffffff",
              borderRadius: "24px",
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => setLightboxOpen(true)}
              aria-label={`View larger ${imageAlt}`}
              sx={{
                width: "100%",
                p: 0,
                border: "none",
                bgcolor: "transparent",
                cursor: "zoom-in",
                borderRadius: "24px",
                transition: "transform 0.2s ease",
                "&:hover": { transform: "scale(1.01)" },
                "&:focus-visible": { outline: "none", boxShadow: "var(--join-focus-ring)" },
              }}
            >
              <Box
                component="img"
                src={previewImage}
                alt={imageAlt}
                sx={{
                  display: "block",
                  width: `${(CAROUSEL_IMAGE_WIDTH / CAROUSEL_DEVICE_WIDTH) * 100}%`,
                  maxWidth: "none",
                  height: "auto",
                  ml: `${-(CAROUSEL_SIDE_PADDING / CAROUSEL_DEVICE_WIDTH) * 100}%`,
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>

          {hasMultipleShots && (
            <>
              <IconButton
                onClick={showPreviousShot}
                aria-label={`Previous ${title} screenshot`}
                sx={{ ...SCREENSHOT_NAV_BUTTON_SX, left: -52 }}
              >
                <KeyboardArrowLeft />
              </IconButton>
              <IconButton
                onClick={showNextShot}
                aria-label={`Next ${title} screenshot`}
                sx={{ ...SCREENSHOT_NAV_BUTTON_SX, right: -52 }}
              >
                <KeyboardArrowRight />
              </IconButton>
              <Box
                aria-hidden
                sx={{ display: "flex", justifyContent: "center", gap: 0.75, mt: 1.5 }}
              >
                {previewImages.map((image, index) => (
                  <Box
                    key={image}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor:
                        index === activeIndex ? "var(--primary-color)" : "var(--join-border-muted)",
                      transition: "background-color 0.2s ease",
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>

        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth={false}
          sx={{
            "& .MuiBackdrop-root": { bgcolor: "rgba(15, 23, 42, 0.78)" },
          }}
          PaperProps={{
            sx: {
              position: "relative",
              bgcolor: "transparent",
              boxShadow: "none",
              overflow: "visible",
              m: 2,
            },
          }}
        >
          <IconButton
            onClick={() => setLightboxOpen(false)}
            aria-label="Close preview"
            sx={{
              position: "absolute",
              top: -14,
              right: -14,
              bgcolor: "rgba(255, 255, 255, 0.96)",
              boxShadow: "var(--join-shadow-card)",
              "&:hover": { bgcolor: "#ffffff" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={previewImage}
            alt={imageAlt}
            sx={{
              maxWidth: "min(520px, 92vw)",
              width: "100%",
              height: "auto",
              display: "block",
              filter: "drop-shadow(0 24px 48px rgba(15, 23, 42, 0.28))",
            }}
          />
        </Dialog>
      </>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 280,
        mx: "auto",
        borderRadius: "28px",
        border: "5px solid var(--join-device-shell)",
        bgcolor: "var(--join-surface-muted)",
        p: 1,
        boxShadow: "var(--join-shadow-elevated)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.25,
          py: 0.75,
          mb: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: "var(--dna-color-palette-black-400)",
            letterSpacing: 0.6,
          }}
        >
          CIVIXTHREAD
        </Typography>
        <Box sx={{ width: 36, height: 5, borderRadius: 3, bgcolor: "var(--join-device-shell)" }} />
      </Box>
      <Box
        sx={{
          borderRadius: "20px",
          bgcolor: "var(--join-device-screen)",
          aspectRatio: "9 / 19",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          px: 2,
          textAlign: "center",
        }}
      >
        <ImageOutlinedIcon
          sx={{ fontSize: 36, color: "var(--dna-color-palette-black-400)" }}
          aria-hidden
        />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--dna-color-palette-black-400)",
            lineHeight: 1.4,
          }}
        >
          Screenshot coming soon
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: "var(--gray-color)",
            lineHeight: 1.4,
          }}
        >
          {title}
        </Typography>
      </Box>
    </Box>
  );
}

function FeatureCarousel({
  heading,
  columns,
  slides,
  ariaLabel,
  idPrefix,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [activeShotIndexes, setActiveShotIndexes] = useState({});
  const maxSteps = slides.length;
  const { fadeUp } = useJoinMotion();

  const handleShotIndexChange = useCallback((slideId, index) => {
    setActiveShotIndexes((prev) => ({ ...prev, [slideId]: index }));
  }, []);

  const handleNext = useCallback(() => {
    setActiveStep((prev) => (prev + 1) % maxSteps);
  }, [maxSteps]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps);
  }, [maxSteps]);

  return (
    <Box
      component={motion.div}
      {...fadeUp}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      sx={{
        borderRadius: "var(--radius-card)",
        border: "1px solid var(--join-border-muted)",
        bgcolor: "var(--join-surface-elevated)",
        boxShadow: "var(--join-shadow-card)",
        overflow: "hidden",
        mb: { xs: 4, md: 5 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, md: 3 },
          py: { xs: 2, md: 2.5 },
          borderBottom: "1px solid var(--join-border-muted)",
          bgcolor: "var(--join-surface-muted)",
          textAlign: "center",
        }}
      >
        <Typography component="h3" sx={{ fontWeight: 800, fontSize: { xs: 20, md: 24 }, color: "var(--black-color)" }}>
          {heading}
        </Typography>
      </Box>

      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 2, md: 8 },
            width: "100%",
            maxWidth: { md: 1080 },
            mx: "auto",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: CAROUSEL_VISIBLE_PHONE_WIDTH,
              flexShrink: 0,
              bgcolor: "#ffffff",
              borderRadius: "24px",
              overflow: "visible",
              px: 0,
            }}
          >
            {slides.map((item, index) => (
              <Box
                key={item.id}
                aria-hidden={index !== activeStep}
                sx={{
                  opacity: index === activeStep ? 1 : 0,
                  visibility: index === activeStep ? "visible" : "hidden",
                  transition: "opacity 0.28s ease",
                  ...(index === activeStep
                    ? { position: "relative" }
                    : { position: "absolute", inset: 0 }),
                }}
              >
                <FeatureScreenshotPlaceholder
                  title={item.title}
                  previewImages={getFeaturePreviews(audienceFromIdPrefix(idPrefix), item.id)}
                  onShotIndexChange={(shotIndex) => handleShotIndexChange(item.id, shotIndex)}
                />
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              width: { xs: "100%", md: 560 },
              maxWidth: "100%",
              minWidth: 0,
              flexShrink: 0,
              pl: { md: 4 },
              pr: { md: 1 },
            }}
          >
            <Box aria-live="polite">
              <Box sx={{ position: "relative", minHeight: { xs: 132, md: 148 }, mb: { xs: 0, md: 2 } }}>
                {slides.map((item, index) => {
                  const shotIndex = activeShotIndexes[item.id] || 0;
                  const currentTitle = item.subShots && item.subShots[shotIndex] ? item.subShots[shotIndex].title : item.title;
                  const currentDescription = item.subShots && item.subShots[shotIndex] ? item.subShots[shotIndex].description : item.description;

                  return (
                  <Box
                    key={item.id}
                    id={`${idPrefix}-feature-panel-${item.id}`}
                    role="tabpanel"
                    aria-labelledby={`${idPrefix}-feature-tab-${item.id}`}
                    aria-hidden={index !== activeStep}
                    sx={{
                      opacity: index === activeStep ? 1 : 0,
                      visibility: index === activeStep ? "visible" : "hidden",
                      transition: "opacity 0.28s ease",
                      ...(index === activeStep
                        ? { position: "relative" }
                        : { position: "absolute", inset: 0 }),
                    }}
                  >
                    <Typography
                      component="h4"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: 22, md: 28 },
                        color: "var(--black-color)",
                        mb: 1.5,
                        lineHeight: 1.2,
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      {currentTitle}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 15, md: 17 },
                        color: "var(--dna-color-palette-black-400)",
                        lineHeight: 1.65,
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      {currentDescription}
                    </Typography>
                  </Box>
                )})}
              </Box>

              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <FeatureTabSelector
                  columns={columns}
                  slides={slides}
                  activeIndex={activeStep}
                  onSelect={setActiveStep}
                  idPrefix={idPrefix}
                  ariaLabel={`${ariaLabel} categories`}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <MobileStepper
        variant="dots"
        steps={maxSteps}
        position="static"
        activeStep={activeStep}
        sx={{
          display: { xs: "flex", md: "none" },
          bgcolor: "var(--join-surface-muted)",
          borderTop: "1px solid var(--join-border-muted)",
          px: 1,
          "& .MuiMobileStepper-dot": {
            bgcolor: "var(--join-border-muted)",
          },
          "& .MuiMobileStepper-dotActive": {
            bgcolor: "var(--primary-color)",
          },
        }}
        nextButton={
          <IconButton
            size="large"
            onClick={handleNext}
            aria-label={`Next ${ariaLabel.toLowerCase()}`}
            sx={CAROUSEL_NAV_BUTTON_SX}
          >
            <KeyboardArrowRight />
          </IconButton>
        }
        backButton={
          <IconButton
            size="large"
            onClick={handleBack}
            aria-label={`Previous ${ariaLabel.toLowerCase()}`}
            sx={CAROUSEL_NAV_BUTTON_SX}
          >
            <KeyboardArrowLeft />
          </IconButton>
        }
      />
    </Box>
  );
}

export default function PlansSection() {
  return (
    <Box id="plans" sx={{ scrollMarginTop: { xs: 120, md: 128 }, pt: { xs: 2, md: 3 }, pb: "var(--join-section-py)" }}>
      <CitizenFirstStatement />

      <Box
        id="citizen-features"
        sx={{
          scrollMarginTop: { xs: 120, md: 128 },
        }}
      >
        <FeatureCarousel
          heading="Tools built for citizens"
          columns={CITIZEN_FEATURE_COLUMNS}
          slides={CITIZEN_FEATURE_SLIDES}
          ariaLabel="Citizen features"
          idPrefix="citizen"
        />
      </Box>

      <Box
        id="organizer-features"
        sx={{
          mt: { xs: 4, md: 5 },
          scrollMarginTop: { xs: 120, md: 128 },
        }}
      >
        <FeatureCarousel
          heading="Tools built for organizers"
          columns={ORG_FEATURE_COLUMNS}
          slides={ORG_FEATURE_SLIDES}
          ariaLabel="Organizer features"
          idPrefix="organizer"
        />
      </Box>

      <EarlyMemberPlanSection />
    </Box>
  );
}
